import { useState, useMemo, useEffect } from "react";
import { toast } from "sonner";
import {
  RotateCcw,
  ArrowRight,
  AlertTriangle,
  X,
  Info,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import { api } from "../../lib/api";
import { badgeTone, errorMessage, getValidPreviousStages, ORDER_WORKFLOW_STAGES, getStageIndex } from "../../lib/format";

const QUICK_REASONS = [
  "Sent to production by mistake.",
  "Design needs correction.",
  "Incorrect approval status.",
  "Wrong production vendor selected.",
  "Client requested changes.",
  "Sent forward by mistake.",
];

export default function MoveBackStageModal({ order, isOpen, onClose, onSuccess, user }) {
  const isBatch = Boolean(order?.isBatch || order?.batch_id);

  // Single order evaluation
  const singleCurrentStage = order?.current_stage || order?.primaryStage || order?.design_stage || "Order Received";
  const singleValidPreviousStages = useMemo(() => {
    if (!order || isBatch) return [];
    const list = getValidPreviousStages(singleCurrentStage);
    return [...list].reverse(); // Show immediate previous stage first
  }, [singleCurrentStage, order, isBatch]);

  // Bulk batch evaluation
  const targetedBatchOrders = useMemo(() => {
    if (!isBatch || !order?.orders) return [];
    if (order.selected_order_ids && order.selected_order_ids.length > 0) {
      const selectedSet = new Set(order.selected_order_ids);
      return order.orders.filter((o) => selectedSet.has(o.order_id));
    }
    return order.orders;
  }, [isBatch, order]);

  const batchEvaluation = useMemo(() => {
    if (!isBatch) return null;
    const items = targetedBatchOrders.map((o) => {
      const curr = o.current_stage || "Order Received";
      const idx = getStageIndex(curr);
      const canMove = idx > 0;
      const targetStage = canMove ? ORDER_WORKFLOW_STAGES[idx - 1] : null;
      return {
        order_id: o.order_id,
        product: o.product || o.order_id,
        quantity: o.quantity,
        unit: o.unit || "pcs",
        current_stage: curr,
        design_stage: o.design_stage || "New",
        can_move_back: canMove,
        target_stage: targetStage,
        ineligible_reason: !canMove ? "Already at initial stage 'Order Received'" : null,
      };
    });

    const eligible = items.filter((it) => it.can_move_back);
    const ineligible = items.filter((it) => !it.can_move_back);

    return {
      items,
      eligible,
      ineligible,
      totalCount: items.length,
      eligibleCount: eligible.length,
      ineligibleCount: ineligible.length,
    };
  }, [isBatch, targetedBatchOrders]);

  const [targetStage, setTargetStage] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isOpen) {
      setReason("");
      setError("");
      if (!isBatch && singleValidPreviousStages.length > 0) {
        setTargetStage(singleValidPreviousStages[0]);
      }
    }
  }, [isOpen, isBatch, singleValidPreviousStages]);

  if (!isOpen || !order) return null;

  // Initial stage check
  if (!isBatch && singleValidPreviousStages.length === 0) {
    return (
      <div className="modal-backdrop" style={{ zIndex: 10000 }}>
        <div className="modal" style={{ maxWidth: 460 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, color: "var(--navy)" }}>Move Back Not Available</h3>
            <button type="button" className="icon-btn" onClick={onClose}><X size={18} /></button>
          </div>
          <p style={{ marginTop: 14, color: "#64748b", fontSize: 13.5, lineHeight: 1.5 }}>
            This order is currently at the initial stage <b>"{singleCurrentStage}"</b> and cannot be moved backward.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
            <button type="button" className="primary-btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (isBatch && batchEvaluation && batchEvaluation.eligibleCount === 0) {
    return (
      <div className="modal-backdrop" style={{ zIndex: 10000 }}>
        <div className="modal" style={{ maxWidth: 480 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <h3 style={{ margin: 0, color: "var(--navy)" }}>Move Back Not Available</h3>
            <button type="button" className="icon-btn" onClick={onClose}><X size={18} /></button>
          </div>
          <p style={{ marginTop: 14, color: "#64748b", fontSize: 13.5, lineHeight: 1.5 }}>
            All {batchEvaluation.totalCount} products in this request are currently at the initial stage <b>"Order Received"</b> and cannot be moved backward.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 20 }}>
            <button type="button" className="primary-btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  const handleQuickReason = (rText) => {
    setReason(rText);
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const cleanReason = reason.trim();
    if (!cleanReason) {
      setError("Reason for moving back is required.");
      return;
    }

    setBusy(true);
    setError("");

    try {
      if (isBatch) {
        const payload = {
          target_stage: "auto",
          reason: cleanReason,
          order_ids: order.selected_order_ids && order.selected_order_ids.length > 0 ? order.selected_order_ids : null,
        };

        const res = await api("post", `/orders/batch/${order.batch_id}/move-back`, payload);
        const data = res.data;

        toast.success(
          `Bulk Request #${order.batch_id}: ${data.updated_count} product${data.updated_count === 1 ? "" : "s"} moved back successfully.`
        );

        if (data.skipped_count > 0) {
          toast.info(`${data.skipped_count} product(s) remained unchanged (already at initial stage).`);
        }

        onSuccess && onSuccess(data);
      } else {
        if (!targetStage) {
          setError("Please select a valid previous stage.");
          setBusy(false);
          return;
        }

        const res = await api("post", `/orders/${order.order_id}/move-back`, {
          target_stage: targetStage,
          reason: cleanReason,
        });

        toast.success(`Order #${order.order_id} moved back from ${singleCurrentStage} → ${targetStage}`);
        onSuccess && onSuccess(res.data);
      }

      onClose();
    } catch (err) {
      console.error("Move back error:", err);
      setError(errorMessage(err, "Could not move order backward"));
    } finally {
      setBusy(false);
    }
  };

  const isSelectedMode = Boolean(order.selected_order_ids && order.selected_order_ids.length > 0);

  return (
    <div className="modal-backdrop" style={{ zIndex: 10000 }}>
      <form
        className="modal"
        onSubmit={handleSubmit}
        style={{ maxWidth: 580 }}
        data-testid="move-back-stage-modal"
      >
        {/* Header */}
        <div className="modal-head" style={{ borderBottom: "1px solid #e2e8f0", paddingBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: 8,
                background: "#fef3c7",
                color: "#b45309",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
              }}
            >
              <RotateCcw size={18} />
            </div>
            <div>
              <div className="small-label" style={{ color: "#b45309" }}>STAGE CORRECTION</div>
              <h2 style={{ margin: 0, fontSize: 18, color: "var(--navy)" }}>
                {isBatch
                  ? isSelectedMode
                    ? `Move Selected Products Back (${batchEvaluation?.totalCount})`
                    : "Move Bulk Request Back (All)"
                  : "Move Order Back"}
              </h2>
            </div>
          </div>
          <button
            type="button"
            className="icon-btn"
            onClick={onClose}
            disabled={busy}
            data-testid="close-move-back-modal"
          >
            <X size={18} />
          </button>
        </div>

        {/* Order / Batch Details Brief */}
        <div
          style={{
            background: "#f8fafc",
            border: "1px solid #e2e8f0",
            borderRadius: 8,
            padding: "10px 14px",
            margin: "14px 0 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 13,
          }}
        >
          <div>
            <span style={{ fontWeight: 700, color: "var(--navy)" }}>
              #{isBatch ? order.batch_id : order.order_id}
            </span>
            <span style={{ color: "#64748b", marginLeft: 6 }}>
              · {isBatch
                ? (isSelectedMode ? `${batchEvaluation?.totalCount} Selected Products` : `${order.orders?.length || 0} Products in Batch`)
                : order.product}
            </span>
          </div>
          <span style={{ color: "#475569", fontWeight: 500 }}>
            {order.client_name || order.client?.company_name}
          </span>
        </div>

        {/* Confirmation Message & Breakdown */}
        {isBatch && batchEvaluation ? (
          <div>
            {/* Confirmation Banner */}
            <div
              style={{
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: 8,
                padding: "12px 14px",
                marginBottom: 14,
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
              }}
            >
              <Info size={18} color="#2563eb" style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontWeight: 700, color: "#1e40af", fontSize: 13, marginBottom: 2 }}>
                  Confirmation: Moving Back to Previous Stage
                </div>
                <div style={{ color: "#1e3a8a", fontSize: 12.5, lineHeight: 1.45 }}>
                  {batchEvaluation.eligibleCount === batchEvaluation.totalCount ? (
                    <>
                      All <b>{batchEvaluation.eligibleCount}</b> product{batchEvaluation.eligibleCount > 1 ? "s" : ""} will be moved back to their respective previous workflow stage in one action.
                    </>
                  ) : (
                    <>
                      <b>{batchEvaluation.eligibleCount}</b> of <b>{batchEvaluation.totalCount}</b> product{batchEvaluation.totalCount > 1 ? "s" : ""} will be moved back to their respective previous workflow stage.
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Ineligible Products Warning */}
            {batchEvaluation.ineligibleCount > 0 && (
              <div
                style={{
                  background: "#fffbeb",
                  border: "1px solid #fde68a",
                  borderRadius: 8,
                  padding: "10px 14px",
                  marginBottom: 14,
                  display: "flex",
                  gap: 8,
                  alignItems: "flex-start",
                }}
              >
                <AlertTriangle size={16} color="#d97706" style={{ flexShrink: 0, marginTop: 2 }} />
                <div style={{ fontSize: 12, color: "#92400e", lineHeight: 1.45 }}>
                  <b>{batchEvaluation.ineligibleCount} product(s) cannot be moved back</b> because they are already at the initial stage ("Order Received"). They will remain unchanged:
                  <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
                    {batchEvaluation.ineligible.map((item) => (
                      <li key={item.order_id}>
                        <b>{item.product}</b> (#{item.order_id})
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}

            {/* Product Movement Breakdown */}
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#475569", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>
                Affected Products & Target Stages
              </div>
              <div
                style={{
                  maxHeight: 180,
                  overflowY: "auto",
                  border: "1px solid #e2e8f0",
                  borderRadius: 6,
                  background: "#ffffff",
                }}
              >
                {batchEvaluation.items.map((item) => (
                  <div
                    key={item.order_id}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 12px",
                      borderBottom: "1px solid #f1f5f9",
                      fontSize: 12,
                    }}
                  >
                    <div>
                      <span style={{ fontWeight: 600, color: "var(--navy)" }}>{item.product}</span>
                      <span style={{ color: "#64748b", marginLeft: 6, fontSize: 11 }}>#{item.order_id}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      {item.can_move_back ? (
                        <>
                          <span className={`badge ${badgeTone(item.current_stage)}`} style={{ fontSize: 10, padding: "1px 6px" }}>
                            {item.current_stage}
                          </span>
                          <ArrowRight size={12} color="#64748b" />
                          <span className={`badge ${badgeTone(item.target_stage)}`} style={{ fontSize: 10, padding: "1px 6px" }}>
                            {item.target_stage}
                          </span>
                        </>
                      ) : (
                        <span style={{ color: "#b45309", fontSize: 11, fontStyle: "italic", background: "#fef3c7", padding: "1px 6px", borderRadius: 4 }}>
                          Unchanged (Initial Stage)
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          /* Single Order Stage Transition Indicator */
          <div
            style={{
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderRadius: 8,
              padding: "14px",
              marginBottom: 16,
            }}
          >
            <div style={{ fontSize: 11, fontWeight: 700, color: "#92400e", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8 }}>
              Stage Movement Confirmation
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                <span style={{ fontSize: 11, color: "#78350f" }}>Current Stage</span>
                <span
                  className={`badge ${badgeTone(singleCurrentStage)}`}
                  style={{ fontSize: 13, fontWeight: 700, padding: "5px 12px" }}
                  data-testid="move-back-current-stage-badge"
                >
                  {singleCurrentStage}
                </span>
              </div>

              <div style={{ color: "#b45309", display: "flex", alignItems: "center", padding: "0 4px" }}>
                <ArrowRight size={20} />
              </div>

              <div style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 160 }}>
                <span style={{ fontSize: 11, color: "#78350f" }}>Move Back To</span>
                {singleValidPreviousStages.length === 1 ? (
                  <span
                    className={`badge ${badgeTone(targetStage)}`}
                    style={{ fontSize: 13, fontWeight: 700, padding: "5px 12px", width: "fit-content" }}
                    data-testid="move-back-target-stage-badge"
                  >
                    {targetStage}
                  </span>
                ) : (
                  <select
                    value={targetStage}
                    onChange={(e) => setTargetStage(e.target.value)}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 6,
                      border: "1px solid #cbd5e1",
                      background: "#ffffff",
                      fontSize: 13,
                      fontWeight: 600,
                      color: "var(--navy)",
                    }}
                    data-testid="select-previous-target-stage"
                  >
                    {singleValidPreviousStages.map((st, i) => (
                      <option key={st} value={st}>
                        {st} {i === 0 ? "(Immediate previous)" : ""}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Mandatory Reason for Moving Back */}
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: "block", fontSize: 13, fontWeight: 600, color: "var(--navy)", marginBottom: 6 }}>
            Reason for Moving Back <span style={{ color: "#e11d48" }}>*</span>
          </label>
          <textarea
            value={reason}
            onChange={(e) => {
              setReason(e.target.value);
              if (error) setError("");
            }}
            placeholder="Please enter a short explanation (e.g. Design was sent to production by mistake, design needs revision, incorrect approval)..."
            rows={3}
            required
            style={{
              width: "100%",
              padding: "10px 12px",
              borderRadius: 6,
              border: error ? "1px solid #f43f5e" : "1px solid #cbd5e1",
              fontSize: 13,
              lineHeight: 1.5,
              resize: "vertical",
              fontFamily: "inherit",
              boxSizing: "border-box",
            }}
            data-testid="move-back-reason-input"
          />

          {/* Quick-Pick Suggestions */}
          <div style={{ marginTop: 8 }}>
            <span style={{ fontSize: 11, color: "#64748b", fontWeight: 600 }}>Quick suggestions:</span>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 4 }}>
              {QUICK_REASONS.map((q) => (
                <button
                  key={q}
                  type="button"
                  onClick={() => handleQuickReason(q)}
                  style={{
                    background: reason === q ? "#e0f2fe" : "#f1f5f9",
                    border: reason === q ? "1px solid #38bdf8" : "1px solid #e2e8f0",
                    color: reason === q ? "#0369a1" : "#475569",
                    borderRadius: 14,
                    padding: "3px 10px",
                    fontSize: 11,
                    cursor: "pointer",
                    transition: "all 0.15s ease",
                  }}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Safe Data Preservation Notice */}
        <div
          style={{
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: 6,
            padding: "10px 12px",
            marginBottom: 16,
            display: "flex",
            gap: 8,
            alignItems: "flex-start",
            fontSize: 12,
            color: "#166534",
            lineHeight: 1.45,
          }}
        >
          <ShieldCheck size={16} color="#16a34a" style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <b>Data Safety Guaranteed:</b> Existing designs, approvals, files, vendor selections, and remarks remain completely intact and will not be deleted.
          </div>
        </div>

        {/* Audit Log Note */}
        <div style={{ fontSize: 11.5, color: "#64748b", marginBottom: 16 }}>
          Action performed by: <b style={{ color: "var(--navy)" }}>{user?.name || "Current User"}</b>. This movement will be permanently recorded in the order audit history.
        </div>

        {error && (
          <div
            style={{
              background: "#fee2e2",
              border: "1px solid #fca5a5",
              color: "#991b1b",
              borderRadius: 6,
              padding: "8px 12px",
              marginBottom: 14,
              fontSize: 12.5,
            }}
            data-testid="move-back-error"
          >
            {error}
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
          <button
            type="button"
            className="outline-btn"
            onClick={onClose}
            disabled={busy}
            data-testid="cancel-move-back-button"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="primary-btn"
            disabled={busy || !reason.trim() || (isBatch && batchEvaluation?.eligibleCount === 0)}
            style={{
              background: !reason.trim() || (isBatch && batchEvaluation?.eligibleCount === 0) ? "#94a3b8" : "#d97706",
              borderColor: !reason.trim() || (isBatch && batchEvaluation?.eligibleCount === 0) ? "#94a3b8" : "#d97706",
              color: "#ffffff",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
            data-testid="confirm-move-back-button"
          >
            <RotateCcw size={14} />
            {busy
              ? "Moving back…"
              : isBatch
              ? `Move ${batchEvaluation?.eligibleCount || 0} Product${batchEvaluation?.eligibleCount === 1 ? "" : "s"} Back`
              : `Move Back to ${targetStage}`}
          </button>
        </div>
      </form>
    </div>
  );
}
